import React, {
  forwardRef,
  ForwardRefRenderFunction,
  HTMLAttributes,
  useImperativeHandle,
  useLayoutEffect,
  useReducer,
  useRef,
} from 'react';
import ScrollBar, { ScrollBarProps } from 'react-perfect-scrollbar';
import { useDebouncedCallback } from '../../hooks/debounce';
import { Button } from '../button/button-component';
import styles from './scrollbar-component.module.scss';
import './scrollbar-component.scss';

export interface CustomScrollbarRef {
  updateScroll(): void;
  getScrollLeft(): number;
  setScrollLeft(x: number): void;
}

type CustomScrollbarProps = {
  showOnLoad?: boolean;
  color?: 'default' | 'purple' | 'light-grey';
  width?: 'default' | 'thick';
  disableHoverAnimation?: boolean;
  showXButtons?: boolean;
  showYButtons?: boolean;
} & ScrollBarProps &
  HTMLAttributes<HTMLElement>;

type CustomScrollbarState = {
  contentWidth: number;
  contentHeight?: number;
  isLeftDisabled: boolean;
  isRightDisabled: boolean;
};

enum CustomScrollbarActionType {
  SetDimensions = 'SetDimensions',
  SetScrollOptions = 'SetScrollOptions',
}

type CustomScrollbarAction =
  | { type: CustomScrollbarActionType.SetDimensions }
  | { type: CustomScrollbarActionType.SetScrollOptions; isRightDisabled: boolean; isLeftDisabled: boolean };

/**
 * Custom X & Y scrollbars with a slim profile.
 *
 * Wrapper for https://github.com/goldenyz/react-perfect-scrollbar + additional props and custom styling.
 */
const CustomScrollbarForwardRef: ForwardRefRenderFunction<CustomScrollbarRef, CustomScrollbarProps> = (
  {
    color = 'default',
    width = 'default',
    disableHoverAnimation = undefined,
    showXButtons = undefined,
    showYButtons = undefined,
    showOnLoad,
    style,
    className,
    children,
    onScroll,
    options,
    ...scrollAttributes
  },
  ref
) => {
  const _contentRef = useRef<HTMLDivElement>(null);
  const _scrollBarRef = useRef<ScrollBar>(null);
  const _scrollBarContainerRef = useRef<HTMLElement | null>(null);
  const debouncedOnScroll = useDebouncedCallback((e: React.UIEvent<HTMLElement, UIEvent>) => onCustomScroll(e, false), 16);

  const [state, dispatch] = useReducer(reducer, {
    contentWidth: 28,
    contentHeight: 28,
    isLeftDisabled: true,
    isRightDisabled: false,
  });

  // update dimensions when contents change
  useLayoutEffect(() => {
    dispatch({ type: CustomScrollbarActionType.SetDimensions });
  }, [children]);

  // expose internal methods
  useImperativeHandle(ref, () => ({
    updateScroll: () => {
      _scrollBarRef.current?.updateScroll();
    },
    getScrollLeft: () => {
      return _scrollBarContainerRef.current?.scrollLeft ?? 0;
    },
    setScrollLeft: (x: number) => {
      if (_contentRef.current) _contentRef.current.scrollLeft = x;
    },
  }));

  return <>{!showXButtons && !showYButtons ? getSimpleScrollbar(scrollAttributes, style) : getScrollbarWithXButtons(scrollAttributes)}</>;

  function reducer(state: CustomScrollbarState, action: CustomScrollbarAction): CustomScrollbarState {
    switch (action.type) {
      case CustomScrollbarActionType.SetDimensions: {
        return {
          ...state,
          contentWidth: showXButtons ? (_contentRef.current?.scrollWidth ?? 0) - 48 - 8 : 28, // container width - buttons - padding
          contentHeight: showYButtons ? (_contentRef.current?.scrollHeight ?? 0) - 48 - 8 : 28, // container width - buttons - padding
        };
      }
      case CustomScrollbarActionType.SetScrollOptions: {
        return {
          ...state,
          isLeftDisabled: action.isLeftDisabled,
          isRightDisabled: action.isRightDisabled,
        };
      }
    }
  }

  function getSimpleScrollbar(scrollAttributes: ScrollBarProps & HTMLAttributes<HTMLElement>, style: React.CSSProperties | undefined) {
    return (
      <ScrollBar
        containerRef={(ref) => {
          _scrollBarContainerRef.current = ref;
        }}
        ref={_scrollBarRef}
        style={style}
        options={{ wheelPropagation: false, ...options }}
        {...scrollAttributes}
        className={`${color} ${width} ${showOnLoad ? 'show-scroll' : ''} ${disableHoverAnimation ? 'no-hover-animation' : ''}  ${
          className ?? ''
        }`}
        onScroll={onScroll}
      >
        {children}
      </ScrollBar>
    );
  }

  function getScrollbarWithXButtons(scrollAttributes: ScrollBarProps & HTMLAttributes<HTMLElement>) {
    return (
      <div className={`${styles['scrollbar-container']} ${className}`} style={style}>
        {/* content */}
        <div
          className={`${styles.content} ${showXButtons ? styles.x : styles.y}`}
          ref={_contentRef}
          tabIndex={0}
          onScroll={debouncedOnScroll}
        >
          {children}
        </div>
        {/* scrollbar w buttons */}
        <div className={`${styles.scroll} ${showXButtons ? styles.x : styles.y}`}>
          {/* left button */}
          <Button
            data-testid='left-button'
            aria-label={showXButtons ? 'Scroll left' : 'Scroll up'}
            onClick={() => onScrollClick(false)}
            icon={showXButtons ? 'chev-left' : 'chev-up'}
            className={`${styles.chevron} ${state.isLeftDisabled ? styles['scroll-button-disabled'] : ''}`}
            disabled={state.isLeftDisabled}
            buttonType='other'
          />

          {/* scrollbar */}
          <ScrollBar
            data-testid='scrollarea'
            containerRef={(ref) => {
              _scrollBarContainerRef.current = ref;
            }}
            ref={_scrollBarRef}
            options={{ wheelPropagation: false, ...options }}
            {...scrollAttributes}
            className={`${color} ${width} ${showOnLoad ? 'show-scroll' : ''} ${disableHoverAnimation ? 'no-hover-animation' : ''}  ${
              className ?? ''
            }`}
            onScroll={(e) => onCustomScroll(e, true)}
          >
            <div style={{ width: state.contentWidth, height: state.contentHeight }}></div>
          </ScrollBar>

          {/* right button */}
          <Button
            data-testid='right-button'
            aria-label={showXButtons ? 'Scroll right' : 'Scroll down'}
            onClick={() => onScrollClick(true)}
            icon={showXButtons ? 'chev-right' : 'chev-down'}
            className={`${styles.chevron} ${state.isRightDisabled ? styles['scroll-button-disabled'] : ''}`}
            disabled={state.isRightDisabled}
            buttonType='other'
          />
        </div>
      </div>
    );
  }

  function onCustomScroll(e: React.UIEvent<HTMLElement, UIEvent>, isFromScrollbar: boolean) {
    if (
      _contentRef.current &&
      _scrollBarContainerRef.current &&
      (_contentRef.current.scrollLeft !== _scrollBarContainerRef.current.scrollLeft ||
        _contentRef.current.scrollTop !== _scrollBarContainerRef.current.scrollTop)
    ) {
      if (isFromScrollbar) {
        _contentRef.current.scrollLeft = _scrollBarContainerRef.current.scrollLeft;
        _contentRef.current.scrollTop = _scrollBarContainerRef.current.scrollTop;
      } else if (_scrollBarContainerRef.current) {
        _scrollBarContainerRef.current.scrollLeft = _contentRef.current.scrollLeft;
        _scrollBarContainerRef.current.scrollTop = _contentRef.current.scrollTop;
      }

      let isRightDisabled: boolean, isLeftDisabled: boolean;

      if (showXButtons) {
        isLeftDisabled = _contentRef.current.scrollLeft === 0;
        isRightDisabled = _contentRef.current.scrollWidth - _contentRef.current.scrollLeft === _contentRef.current.clientWidth;
      } else {
        isLeftDisabled = _contentRef.current.scrollTop === 0;
        isRightDisabled =
          Math.floor(_contentRef.current.scrollHeight - _contentRef.current.scrollTop) === _contentRef.current.clientHeight ||
          Math.ceil(_contentRef.current.scrollHeight - _contentRef.current.scrollTop) === _contentRef.current.clientHeight;
      }

      dispatch({ type: CustomScrollbarActionType.SetScrollOptions, isRightDisabled, isLeftDisabled });

      onScroll?.(e);
    }
  }

  function onScrollClick(isToTheRight: boolean) {
    if (_scrollBarContainerRef.current) {
      if (showXButtons) _scrollBarContainerRef.current.scrollLeft += (!isToTheRight ? -1 : 1) * 25;
      else {
        _scrollBarContainerRef.current.scrollTop += (!isToTheRight ? -1 : 1) * 25;
      }
    }
  }
};

export const CustomScrollbar = forwardRef(CustomScrollbarForwardRef);

export default CustomScrollbar;
